import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, Building2, ChevronRight, Shield, Users, ChevronDown, Mail, Tag, Plus, Trash2, Copy, Check, Inbox, AlertCircle } from "lucide-react";

function fmtDate(s) {
  if (!s) return "";
  try { return new Date(s).toLocaleString("ja-JP", { dateStyle: "short", timeStyle: "short" }); } catch { return s; }
}

const STATUS_LABEL = { new: "未対応", contacted: "対応中", converted: "成約", lost: "見送り" };
const STATUS_COLOR = {
  new: "bg-amber-100 text-amber-800",
  contacted: "bg-indigo-100 text-indigo-800",
  converted: "bg-emerald-100 text-emerald-800",
  lost: "bg-stone-100 text-stone-600",
};

export default function SuperAdminDashboard() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState("tenants");
  const [err, setErr] = useState("");

  const onLogout = async () => { await logout(); nav("/super/login"); };

  return (
    <div className="min-h-screen bg-soft-indigo">
      <header className="bg-white border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="text-indigo-500" size={20} />
            <div>
              <div className="text-xs text-stone-500">運営者</div>
              <div className="font-display text-lg font-bold text-stone-900">運営ダッシュボード</div>
            </div>
          </div>
          <button onClick={onLogout} data-testid="super-logout-btn" className="rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 px-4 py-2 text-sm font-semibold inline-flex items-center gap-2"><LogOut size={14} />ログアウト</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
          {[
            ["tenants", "テナント", <Building2 key="t" size={14} />],
            ["addon-requests", "オプション申込み", <Inbox key="ar" size={14} />],
            ["leads", "リード", <Mail key="l" size={14} />],
            ["coupons", "クーポン", <Tag key="c" size={14} />],
          ].map(([k, l, ic]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`super-tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap inline-flex items-center gap-1.5 ${tab === k ? "bg-indigo-500 text-white shadow" : "bg-white border border-stone-200 text-stone-700 hover:bg-stone-50"}`}>
              {ic}{l}
            </button>
          ))}
        </div>

        {err && <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-2.5 rounded-xl text-sm">{err}</div>}

        {tab === "tenants" && <TenantsTab setErr={setErr} />}
        {tab === "addon-requests" && <AddonRequestsTab setErr={setErr} />}
        {tab === "leads" && <LeadsTab setErr={setErr} />}
        {tab === "coupons" && <CouponsTab setErr={setErr} />}
      </main>
    </div>
  );
}

function AddonRequestsTab({ setErr }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/super/addon-requests"); setItems(data.items || []); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const activate = async (it, applyStripe) => {
    const stripeNote = applyStripe ? "\nStripeにも月額アドオンを追加し、ただちに課金が開始されます。" : "\nStripeは変更しません（機能のみ有効化）。";
    if (!window.confirm(`${it.tenant_name}（${it.tenant_slug}）に「${it.label}」を有効化します。${stripeNote}\nよろしいですか？`)) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.post(`/super/tenants/${it.tenant_id}/addons/activate`, { addon: it.addon, apply_stripe: applyStripe });
      setMsg(`${it.tenant_name} の「${it.label}」を有効化しました`);
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const reject = async (it) => {
    if (!window.confirm(`${it.tenant_name} の「${it.label}」申込みを却下しますか？\n（お客様には画面表示が「申込む」に戻ります。メール通知はされません）`)) return;
    setBusy(true); setErr(""); setMsg("");
    try {
      await api.post(`/super/tenants/${it.tenant_id}/addons/reject`, { addon: it.addon });
      setMsg(`${it.tenant_name} の申込みを却下しました`);
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <section data-testid="addon-requests-tab">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="font-display text-xl font-bold text-stone-900 inline-flex items-center gap-2"><Inbox size={18} /> オプション申込み一覧</h2>
        <div className="text-xs text-stone-600">受付件数: <strong className="text-stone-900">{items.length}</strong></div>
      </div>
      {msg && <div className="mb-3 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5 rounded-xl text-sm">{msg}</div>}
      {items.length === 0 ? (
        <div className="card-soft p-8 text-center text-stone-500 text-sm">現在、申込みはありません</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const isMonthly = it.addon === "multi" || it.addon === "pin";
            const hasSub = it.subscription_status === "active" || it.subscription_status === "trialing";
            return (
              <div key={`${it.tenant_id}_${it.addon}`} className="card-soft p-5" data-testid={`request-${it.tenant_slug}-${it.addon}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-[260px]">
                    <div className="font-display font-bold text-stone-900 text-base">{it.label}</div>
                    <div className="text-sm text-stone-700 mt-1">{it.tenant_name} <span className="text-stone-400">/</span> <code className="text-xs bg-stone-100 px-1.5 py-0.5 rounded">{it.tenant_slug}</code></div>
                    <div className="text-xs text-stone-500 mt-1">サブスク: <span className={`tag ${hasSub ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{it.subscription_status || "なし"}</span></div>
                    {it.phone && <div className="text-xs text-stone-500 mt-1">電話: {it.phone}</div>}
                    {it.note && <div className="text-xs text-stone-700 mt-2 bg-stone-50 p-2 rounded whitespace-pre-wrap"><strong>備考:</strong> {it.note}</div>}
                    <div className="text-xs text-stone-400 mt-2">申込: {fmtDate(it.requested_at)} / {it.requested_by}</div>
                  </div>
                  <div className="flex flex-col gap-2 min-w-[180px]">
                    {isMonthly ? (
                      <>
                        <button onClick={() => activate(it, true)} disabled={busy || !hasSub} data-testid={`activate-stripe-${it.tenant_slug}-${it.addon}`}
                          className="rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold px-4 py-2">
                          有効化 + Stripe課金開始
                        </button>
                        <button onClick={() => activate(it, false)} disabled={busy} data-testid={`activate-only-${it.tenant_slug}-${it.addon}`}
                          className="rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 text-xs font-semibold px-4 py-2">
                          機能のみ有効化（請求なし）
                        </button>
                        {!hasSub && <div className="text-[10px] text-amber-700 inline-flex items-center gap-1"><AlertCircle size={11} /> サブスク未開始のため Stripe 課金は不可</div>}
                      </>
                    ) : (
                      <button onClick={() => activate(it, false)} disabled={busy} data-testid={`activate-only-${it.tenant_slug}-${it.addon}`}
                        className="rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-bold px-4 py-2">
                        有効化
                      </button>
                    )}
                    <button onClick={() => reject(it)} disabled={busy} data-testid={`reject-${it.tenant_slug}-${it.addon}`}
                      className="rounded-full bg-rose-100 hover:bg-rose-200 text-rose-700 text-xs font-semibold px-4 py-2">
                      却下
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-4 text-xs text-stone-500 leading-relaxed">
        <strong>説明:</strong>「<u>有効化 + Stripe課金開始</u>」を押すと、月額オプションが Stripe サブスクに追加され、日割り課金が即時開始されます。
        「<u>機能のみ有効化</u>」は Stripe を変更せず、DBのフラグだけONにします（テスト・無償提供などに使用）。
        カスタムCSV (¥15,000 買い切り) はStripe Checkoutでお客様に別途課金されます。「有効化」だけ押すと、課金なしで機能を付与します。
      </div>
    </section>
  );
}

function TenantsTab({ setErr }) {
  const [tenants, setTenants] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [usersMap, setUsersMap] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try { const { data } = await api.get("/super/tenants"); setTenants(data.items || []); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggleTenant = async (t) => {
    if (expanded === t.id) { setExpanded(null); return; }
    setExpanded(t.id);
    if (!usersMap[t.id]) {
      const { data } = await api.get(`/super/tenants/${t.id}/users`);
      setUsersMap({ ...usersMap, [t.id]: data.items || [] });
    }
  };

  const impersonate = async (uid, slug) => {
    try {
      const { data } = await api.post(`/auth/impersonate/${uid}`);
      if (data?.token) localStorage.setItem("kintarou_token", data.token);
      const dest = data.user.role === "tenant_admin" ? `/t/${slug}/admin` : `/t/${slug}/`;
      window.location.assign(dest);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (t) => {
    if (!window.confirm(`「${t.name}」を完全に削除します。\n所属する従業員・打刻データもすべて消去されます。\nよろしいですか？`)) return;
    try {
      await api.delete(`/super/tenants/${t.id}`);
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const resetBillingCache = async () => {
    if (!window.confirm("Stripeの商品ID・顧客IDのキャッシュをすべて消去します。\nテストモード/本番モードの切替時に使用してください。\n\n※全テナントのサブスク状況が『未登録』に戻ります。各テナントは再度カード登録が必要になります。\n\nよろしいですか？")) return;
    try {
      const { data } = await api.post("/super/billing/reset-cache");
      alert(`完了しました\n商品キャッシュ: ${data.billing_config_cleared}件削除\n決済履歴: ${data.payment_transactions_cleared}件削除\nテナント: ${data.tenants_reset}件リセット`);
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const syncBilling = async () => {
    if (!window.confirm("全テナントのサブスク状況をStripeから取得して同期します。\nWebhookの取りこぼし・誤判定を修正したいときに使用してください。\n\nよろしいですか？")) return;
    try {
      const { data } = await api.post("/super/billing/sync");
      alert(`同期完了\n同期: ${data.synced}件 / 合計: ${data.total_with_sub}件\nエラー: ${data.errors}件`);
      load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const initStripePrices = async () => {
    if (!window.confirm("Stripeに商品(基本プラン・複数拠点・スタッフPIN・カスタムCSV)を事前作成します。\nStripeダッシュボードに4つの商品が並ぶようになります。\n\n既に作成済みの商品は重複作成されません。\n\nよろしいですか？")) return;
    try {
      const { data } = await api.post("/super/billing/init-prices");
      alert(`Stripe商品の作成・確認が完了しました\n\n基本プラン: ${data.base_price_id}\n複数拠点アドオン: ${data.addon_price_id}\nスタッフPINアドオン: ${data.pin_price_id}\nカスタムCSV: ${data.custom_csv_price_id}\n\nStripeダッシュボード→「商品」で確認できます。`);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h1 className="font-display text-2xl font-black text-stone-900">テナント一覧</h1>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={initStripePrices} data-testid="init-stripe-prices-btn"
            className="rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-900 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
            Stripe商品を初期化
          </button>
          <button onClick={syncBilling} data-testid="sync-billing-btn"
            className="rounded-full bg-indigo-100 hover:bg-indigo-200 text-indigo-900 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
            Stripe同期
          </button>
          <button onClick={resetBillingCache} data-testid="reset-billing-cache-btn"
            className="rounded-full bg-amber-100 hover:bg-amber-200 text-amber-900 text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">
            Stripeキャッシュ初期化
          </button>
          <div className="text-sm text-stone-500" data-testid="tenant-count">合計 {tenants.length} 社</div>
        </div>
      </div>

      {/* Status summary + filter */}
      {tenants.length > 0 && (
        <div className="mb-4 grid grid-cols-2 md:grid-cols-5 gap-2">
          {[
            { key: "all", label: "すべて", count: tenants.length, color: "bg-white border-stone-200 text-stone-700" },
            { key: "paid", label: "有料プラン", count: tenants.filter(t => t.subscription_status === "active" || t.subscription_status === "trialing").length, color: "bg-emerald-50 border-emerald-200 text-emerald-800" },
            { key: "trial", label: "無料お試し中", count: tenants.filter(t => t.in_signup_trial && t.subscription_status !== "active" && t.subscription_status !== "trialing").length, color: "bg-amber-50 border-amber-200 text-amber-800" },
            { key: "expired", label: "期限切れ", count: tenants.filter(t => !t.in_signup_trial && t.subscription_status !== "active" && t.subscription_status !== "trialing" && t.subscription_status !== "past_due").length, color: "bg-rose-50 border-rose-200 text-rose-800" },
            { key: "past_due", label: "支払い遅延", count: tenants.filter(t => t.subscription_status === "past_due").length, color: "bg-rose-50 border-rose-200 text-rose-800" },
          ].map((f) => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)} data-testid={`status-filter-${f.key}`}
              className={`rounded-2xl border-2 px-3 py-2 text-left ${f.color} ${statusFilter === f.key ? "ring-2 ring-indigo-300" : ""}`}>
              <div className="text-xs">{f.label}</div>
              <div className="font-display text-xl font-black tabular-nums">{f.count}</div>
            </button>
          ))}
        </div>
      )}
      <div className="space-y-3">
        {tenants
          .filter((t) => {
            const paid = t.subscription_status === "active" || t.subscription_status === "trialing";
            if (statusFilter === "all") return true;
            if (statusFilter === "paid") return paid;
            if (statusFilter === "trial") return t.in_signup_trial && !paid;
            if (statusFilter === "expired") return !paid && !t.in_signup_trial && t.subscription_status !== "past_due";
            if (statusFilter === "past_due") return t.subscription_status === "past_due";
            return true;
          })
          .map((t) => (
          <div key={t.id} className="card-soft overflow-hidden" data-testid={`tenant-card-${t.slug}`}>
            <button onClick={() => toggleTenant(t)} data-testid={`tenant-expand-${t.slug}`} className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-stone-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500"><Building2 size={20} /></div>
                <div>
                  <div className="font-display font-bold text-stone-900">{t.name}</div>
                  <div className="text-xs text-stone-500">/t/{t.slug}/login &middot; <Users size={11} className="inline" /> {t.user_count} 名</div>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                <SubBadge tenant={t} />
                <span className={`tag ${t.allow_impersonation ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`}>代理ログイン: {t.allow_impersonation ? "許可" : "不可"}</span>
                <span onClick={(e) => { e.stopPropagation(); remove(t); }} data-testid={`delete-tenant-${t.slug}`}
                  className="p-1.5 rounded-full hover:bg-rose-50 text-rose-500 cursor-pointer" title="テナントを削除">
                  <Trash2 size={14} />
                </span>
                <ChevronDown size={18} className={`transition-transform ${expanded === t.id ? "rotate-180" : ""}`} />
              </div>
            </button>
            {expanded === t.id && (
              <div className="border-t border-stone-100 bg-stone-50/50 p-5">
                {!t.allow_impersonation && <div className="mb-3 text-xs text-stone-500 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">この企業は代理ログインを許可していません。</div>}
                <table className="w-full text-sm">
                  <thead className="text-stone-500"><tr><th className="text-left py-2 px-2">氏名</th><th className="text-left">メール</th><th className="text-left">役割</th><th></th></tr></thead>
                  <tbody>
                    {(usersMap[t.id] || []).map((u) => (
                      <tr key={u.id} className="border-t border-stone-100" data-testid={`super-user-${u.id}`}>
                        <td className="py-2 px-2 font-semibold">{u.name}</td>
                        <td>{u.email}</td>
                        <td><span className={`tag ${u.role === "tenant_admin" ? "bg-indigo-100 text-indigo-800" : "bg-stone-100 text-stone-700"}`}>{u.role === "tenant_admin" ? "管理者" : "従業員"}</span></td>
                        <td className="text-right pr-2">
                          <button data-testid={`impersonate-${u.id}`} disabled={!t.allow_impersonation} onClick={() => impersonate(u.id, t.slug)}
                            className="rounded-full bg-indigo-500 hover:bg-indigo-600 disabled:bg-stone-200 disabled:text-stone-500 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1">代理ログイン <ChevronRight size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))}
        {tenants.length === 0 && <div className="card-soft p-10 text-center text-stone-500">テナントはまだ登録されていません。</div>}
      </div>
    </div>
  );
}

function SubBadge({ tenant }) {
  const s = tenant.subscription_status;
  if (s === "active") {
    return <span className="tag bg-emerald-100 text-emerald-800" data-testid={`sub-status-${tenant.slug}`}>有料プラン</span>;
  }
  if (s === "trialing") {
    return <span className="tag bg-emerald-100 text-emerald-800" data-testid={`sub-status-${tenant.slug}`}>Stripe試用中</span>;
  }
  if (tenant.in_signup_trial) {
    return <span className="tag bg-amber-100 text-amber-800" data-testid={`sub-status-${tenant.slug}`}>無料お試し中 (残り{tenant.signup_trial_days_left}日)</span>;
  }
  if (s === "past_due") {
    return <span className="tag bg-rose-100 text-rose-800" data-testid={`sub-status-${tenant.slug}`}>支払い遅延</span>;
  }
  if (s === "canceled") {
    return <span className="tag bg-stone-200 text-stone-700" data-testid={`sub-status-${tenant.slug}`}>解約済み</span>;
  }
  return <span className="tag bg-rose-100 text-rose-800" data-testid={`sub-status-${tenant.slug}`}>期限切れ</span>;
}

const INTEREST_LABEL = {
  trial: "トライアル希望",
  multi_location: "複数拠点・部署",
  custom_csv: "カスタムCSV",
  "31plus": "31名以上",
  other: "その他",
};

function LeadsTab({ setErr }) {
  const [data, setData] = useState({ items: [], counts: { total: 0, new: 0, contacted: 0, converted: 0 } });
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [issueFor, setIssueFor] = useState(null); // lead id

  const load = async () => {
    try {
      const { data } = await api.get("/super/leads", { params: filter ? { status: filter } : {} });
      setData(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, status) => {
    try { await api.put(`/super/leads/${id}`, { status }); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const saveNote = async (id, note) => {
    try { await api.put(`/super/leads/${id}`, { note }); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const remove = async (id) => {
    if (!window.confirm("このリードを削除しますか？")) return;
    try { await api.delete(`/super/leads/${id}`); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl font-black text-stone-900">リード管理</h1>
          <div className="text-xs text-stone-500 mt-1">LPのお問い合わせフォームから届いた見込み客一覧</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Kpi label="未対応" value={data.counts.new} tone="amber" />
          <Kpi label="対応中" value={data.counts.contacted} tone="indigo" />
          <Kpi label="成約" value={data.counts.converted} tone="emerald" />
          <Kpi label="合計" value={data.counts.total} tone="stone" />
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="lead-filters">
        {[["", "すべて"], ["new", "未対応"], ["contacted", "対応中"], ["converted", "成約"], ["lost", "見送り"]].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setFilter(k)} data-testid={`lead-filter-${k || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${filter === k ? "bg-stone-900 text-white" : "bg-white border border-stone-200 text-stone-700"}`}>{l}</button>
        ))}
      </div>

      <div className="space-y-2">
        {data.items.map((l) => (
          <div key={l.id} className="card-soft overflow-hidden" data-testid={`lead-${l.id}`}>
            <button onClick={() => setExpanded(expanded === l.id ? null : l.id)} className="w-full px-5 py-3 flex items-center justify-between gap-3 hover:bg-stone-50 text-left">
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 shrink-0"><Mail size={16} /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-stone-900 truncate">{l.name}{l.company ? ` ／ ${l.company}` : ""}</div>
                  <div className="text-xs text-stone-500 truncate">{l.email} &middot; {fmtDate(l.created_at)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="tag bg-stone-100 text-stone-700 text-[10px]">{INTEREST_LABEL[l.interest] || l.interest}</span>
                <span className={`tag ${STATUS_COLOR[l.status] || "bg-stone-100"}`}>{STATUS_LABEL[l.status] || l.status}</span>
                {l.coupon_code && <span className="tag bg-emerald-100 text-emerald-800">CP: {l.coupon_code}</span>}
                <ChevronDown size={16} className={`transition-transform ${expanded === l.id ? "rotate-180" : ""}`} />
              </div>
            </button>
            {expanded === l.id && (
              <div className="border-t border-stone-100 bg-stone-50/50 p-5 space-y-3">
                {l.phone && <div className="text-sm"><span className="text-stone-500">電話:</span> {l.phone}</div>}
                {l.message && <div className="text-sm whitespace-pre-wrap bg-white border border-stone-100 rounded-xl p-3">{l.message}</div>}
                <div>
                  <div className="text-xs text-stone-500 mb-1">メモ</div>
                  <textarea defaultValue={l.note || ""} onBlur={(e) => saveNote(l.id, e.target.value)} rows={2} placeholder="社内メモ..." className="w-full px-3 py-2 rounded-xl border border-stone-200 bg-white text-sm" data-testid={`lead-note-${l.id}`} />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-stone-500">ステータス：</span>
                  {["new", "contacted", "converted", "lost"].map((s) => (
                    <button key={s} onClick={() => updateStatus(l.id, s)} data-testid={`lead-status-${s}-${l.id}`}
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${l.status === s ? STATUS_COLOR[s] : "bg-white border border-stone-200 text-stone-600"}`}>{STATUS_LABEL[s]}</button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  <a href={`mailto:${l.email}`} className="rounded-full bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-semibold px-4 py-1.5 inline-flex items-center gap-1"><Mail size={12} />メールを書く</a>
                  <button onClick={() => setIssueFor(l)} data-testid={`lead-issue-coupon-${l.id}`} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold px-4 py-1.5 inline-flex items-center gap-1"><Tag size={12} />クーポン発行</button>
                  <button onClick={() => remove(l.id)} className="rounded-full bg-rose-500 hover:bg-rose-600 text-white text-xs font-semibold px-4 py-1.5 inline-flex items-center gap-1"><Trash2 size={12} />削除</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {data.items.length === 0 && <div className="card-soft p-10 text-center text-stone-500">リードはまだありません。</div>}
      </div>

      {issueFor && <IssueCouponModal lead={issueFor} onClose={() => { setIssueFor(null); load(); }} setErr={setErr} />}
    </div>
  );
}

function IssueCouponModal({ lead, onClose, setErr }) {
  const [form, setForm] = useState({
    description: lead ? `${lead.name}様向け特別クーポン` : "",
    discount_type: "free_months",
    discount_value: 1,
    max_uses: 1,
    valid_from: "",
    valid_until: "",
    code: "",
  });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const payload = { ...form, lead_id: lead?.id || null };
      if (!payload.code) delete payload.code;
      if (!payload.valid_from) delete payload.valid_from;
      if (!payload.valid_until) delete payload.valid_until;
      payload.discount_value = Number(payload.discount_value);
      payload.max_uses = payload.max_uses ? Number(payload.max_uses) : null;
      const { data } = await api.post("/super/coupons", payload);
      setCreated(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl max-w-md w-full p-6 md:p-7" onClick={(e) => e.stopPropagation()} data-testid="issue-coupon-modal">
        <div className="font-display text-xl font-black text-stone-900 mb-4">クーポン発行 {lead && `（${lead.name}様）`}</div>
        {created ? (
          <div className="space-y-4">
            <div className="bg-emerald-50 border-2 border-dashed border-emerald-300 rounded-2xl p-5 text-center">
              <div className="text-xs text-emerald-700 font-semibold">クーポンコード</div>
              <div className="font-display font-black text-3xl text-emerald-700 tracking-wider mt-1" data-testid="created-coupon-code">{created.code}</div>
              <button onClick={() => navigator.clipboard?.writeText(created.code)} className="mt-3 text-xs text-emerald-700 inline-flex items-center gap-1 hover:underline"><Copy size={12} />コピー</button>
            </div>
            <p className="text-xs text-stone-500 text-center">このコードをメールでお客様に送付してください。</p>
            <button onClick={onClose} className="w-full rounded-full bg-stone-900 text-white font-semibold py-2.5">閉じる</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Lbl label="説明" required>
              <input data-testid="coupon-desc" required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="inp2" />
            </Lbl>
            <div className="grid grid-cols-2 gap-3">
              <Lbl label="割引種別">
                <select data-testid="coupon-type" value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className="inp2">
                  <option value="free_months">無料月数</option>
                  <option value="percent">割引率(%)</option>
                  <option value="amount">割引額(円)</option>
                </select>
              </Lbl>
              <Lbl label="値" required>
                <input data-testid="coupon-value" required type="number" min="1" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="inp2" />
              </Lbl>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Lbl label="利用上限">
                <input data-testid="coupon-max" type="number" min="1" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} className="inp2" />
              </Lbl>
              <Lbl label="カスタムコード(任意)">
                <input data-testid="coupon-code" type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="自動生成" className="inp2 uppercase" />
              </Lbl>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Lbl label="開始日（任意）">
                <input data-testid="coupon-from" type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} className="inp2" />
              </Lbl>
              <Lbl label="有効期限（任意）">
                <input data-testid="coupon-until" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="inp2" />
              </Lbl>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={onClose} className="flex-1 rounded-full bg-stone-100 text-stone-700 font-semibold py-2.5">キャンセル</button>
              <button type="submit" data-testid="submit-coupon" disabled={busy} className="flex-1 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold py-2.5">{busy ? "発行中..." : "発行する"}</button>
            </div>
            <style>{`.inp2{width:100%; padding:.5rem .75rem; border-radius:.625rem; border:1px solid #E7E5E4; background:#FAFAF9; outline:none; font-size:.875rem;} .inp2:focus{background:#fff; box-shadow:0 0 0 3px rgba(74,222,128,.25);}`}</style>
          </form>
        )}
      </div>
    </div>
  );
}

function CouponsTab({ setErr }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/super/coupons"); setItems(data.items || []); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const remove = async (id) => {
    if (!window.confirm("このクーポンを削除しますか？")) return;
    try { await api.delete(`/super/coupons/${id}`); load(); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const labelType = (t, v) => ({ free_months: `${v}ヶ月無料`, percent: `${v}% OFF`, amount: `¥${v} OFF` })[t] || `${v}`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display text-2xl font-black text-stone-900">クーポン</h1>
        <button onClick={() => setShowForm(true)} data-testid="new-coupon-btn" className="rounded-full bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 text-sm font-semibold inline-flex items-center gap-1"><Plus size={14} />新規発行</button>
      </div>
      <div className="card-soft p-4 md:p-6">
        <table className="w-full text-sm">
          <thead className="text-stone-500"><tr><th className="text-left py-2 px-2">コード</th><th className="text-left">説明</th><th className="text-left">割引</th><th className="text-left">利用</th><th className="text-left">有効期間</th><th></th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-stone-100" data-testid={`coupon-row-${c.id}`}>
                <td className="py-2 px-2"><span className="font-mono font-bold text-emerald-700">{c.code}</span></td>
                <td className="text-stone-700">{c.description}</td>
                <td>{labelType(c.discount_type, c.discount_value)}</td>
                <td>{c.uses} / {c.max_uses ?? "∞"}</td>
                <td className="text-xs text-stone-500">{c.valid_from || "—"} 〜 {c.valid_until || "無期限"}</td>
                <td className="text-right pr-2">
                  <button onClick={() => navigator.clipboard?.writeText(c.code)} className="p-2 rounded-full hover:bg-stone-100 text-stone-500" title="コピー"><Copy size={14} /></button>
                  <button onClick={() => remove(c.id)} className="p-2 rounded-full hover:bg-rose-50 text-rose-500" data-testid={`coupon-del-${c.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-stone-500">クーポンはまだ発行されていません</td></tr>}
          </tbody>
        </table>
      </div>
      {showForm && <IssueCouponModal lead={null} onClose={() => { setShowForm(false); load(); }} setErr={setErr} />}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const c = { amber: "bg-amber-50 text-amber-800 border-amber-200", indigo: "bg-indigo-50 text-indigo-800 border-indigo-200", emerald: "bg-emerald-50 text-emerald-800 border-emerald-200", stone: "bg-white text-stone-800 border-stone-200" }[tone];
  return (
    <div className={`rounded-xl border ${c} px-3 py-1.5 inline-flex items-center gap-2`} data-testid={`kpi-${label}`}>
      <span className="text-xs">{label}</span>
      <span className="font-display font-black">{value}</span>
    </div>
  );
}

function Lbl({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-stone-700">{label}{required && <span className="text-rose-500 ml-0.5">*</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
